/**
 * Component: AccountDropdown
 * @description Displays a dropdown menu for the user's account options, including navigation to account details and a sign-out button. Handles user logout and redirection to the login page.
 *
 * @props
 *   @param {function} onSignOut - Callback function to close the dropdown when the user signs out.
 *
 * @redux
 *   @selector currentUser - Retrieves the current user's information from the Redux store.
 *   @action logout - Dispatches an action to clear the user session and perform logout.
 *
 * @methods
 *   @function handleSignOut - Calls the `logout` action, triggers `onSignOut`, and navigates the user to the login page.
 *
 * @navigation
 *   - Uses `useNavigate` from `react-router-dom` to redirect the user to different pages.
 *
 * @components
 *   - AccountButton: Button that navigates to the account details page when clicked.
 *   - SignOutButton: Button that logs the user out and redirects to the login page.
 *   - Avatar: Displays the user's profile image or a default avatar if no image is available.
 *
 * @layout
 *   - AccountContainer: Wrapper for the dropdown content, including account details and the sign-out button.
 *   - Divider: Separates the account details section from the sign-out button.
 *
 * @example
 * <AccountDropdown onSignOut={() => console.log("Dropdown closed")} />
 */

import React from 'react';
import { AccountContainer, SignOutButton } from './AccountDropdown.styles';
import { useDispatch } from 'react-redux';
import { logout } from '../../redux/actions/userActions';
import { useNavigate } from 'react-router-dom';

const AccountDropdown = ({ onSignOut }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleSignOut = () => {
    // Call the logout action to clear the user session and local storage
    dispatch(logout());
    onSignOut(); // Close the dropdown
    // Redirect to the login page
    navigate('/login');
  };

  return (
    <AccountContainer>
      <SignOutButton onClick={handleSignOut}>Sign out</SignOutButton>
    </AccountContainer>
  );
};

export default AccountDropdown;
